import React, { useEffect, useState } from 'react';
import { Autocomplete, Paper, Popper } from '@mui/material';
import { TextField } from '@mui/material';
import { inputsStyle } from './styles';
import { ErrorText, FindLoad, Images, useGlobalStyles } from 'utils';
import { ErrMessage } from '../messages';
import { InputMinLoader } from './inputMiniLoader';
import { TextRow } from '../table';
import { MinLoader } from '../loader';

// `wideMenu`: Autocomplete sets `style.width` to the field's width; keep that as the minimum and
// let the menu fit its longest option, left-aligned with the field, within the screen.
const WideMenuPopper = ({ style, ...props }) => (
    <Popper
        {...props}
        placement="bottom-start"
        style={{ ...style, width: 'max-content', minWidth: style?.width, maxWidth: 'calc(100vw - 32px)' }}
    />
);

export const SelectTypeAutocomplete = ({
    list,
    title,
    defaultValue,
    name,
    handleSelect,
    style,
    disabled,
    error,
    typeError,
    handleType,
    loadType,
    placeholder,
    renderValue,
    customClass,
    uiType,
    outLabel,
    noError,
    outStyle,
    renderStatus,
    startIcon,
    CustomPaperComponent,
    defaultSelected,
    refresh,
    customOptions,
    renderFieldValue,
    disableClearable,
    renderAllValue,
    variant,
    popperZIndex,
    groupBy,
    renderGroup,
    // The menu may be wider than the field (up to the screen) and its option names wrap instead
    // of being cut with "…" — for a narrow field whose option names are long (template Use Case).
    wideMenu,
}) => {
    const classes = inputsStyle();
    const globalStyles = useGlobalStyles();
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const loader = FindLoad(loadType);
    const current = list?.find((i) => i.id === defaultValue);
    const currentSelected = current ? current : defaultSelected;

    const handleClick = (e, ev) => {
        setValue(ev);
        if (ev) {
            let info = { target: { value: renderAllValue ? ev : ev.id, name: name } };
            handleSelect(info);
            setValue(ev.id);
        } else {
            let info = { target: { value: '', name: name } };
            handleSelect(info);
            setValue('');
        }
    };

    useEffect(() => {
        if (!currentSelected) {
            if (defaultValue && list?.length) {
                list?.find((i) => {
                    if (i.id === defaultValue || i._id === defaultValue) {
                        setValue(i);
                    }
                });
            } else {
                setValue(null);
            }
        }

        if (currentSelected && defaultValue) {
            setValue(currentSelected);
        } else if (currentSelected && !defaultValue) {
            setValue(null);
        }
    }, [defaultValue, list, currentSelected]);

    return (
        <div className={style ? style : ''}>
            {outLabel && (
                <p id='labelTitle' className={classes.labelTitle} style={{ ...outStyle }}>
                    {outLabel}
                </p>
            )}
            {renderFieldValue && !open && renderFieldValue}
            <Autocomplete
                slotProps={popperZIndex ? { popper: { style: { zIndex: popperZIndex } } } : undefined}
                // Autocomplete pins the menu to the field's width; `wideMenu` lets it grow to its
                // longest option, never narrower than the field nor wider than the screen.
                PopperComponent={wideMenu ? WideMenuPopper : undefined}
                disableClearable={disableClearable}
                id="disable-close-on-select"
                name={name}
                loading={!!loader?.length}
                style={{ width: '100%' }}
                className={
                    uiType
                        ? error === name
                            ? classes.modalInputTextFieldError
                            : classes.noChipLoads
                        : disabled
                          ? classes.inputTextFieldDisabled
                          : customClass
                            ? customClass
                            : error === name
                              ? globalStyles.inputTextFieldError
                              : defaultValue
                                ? globalStyles.inputTextFieldBlue
                                : classes.inputTextField
                }
                getOptionLabel={(option) => renderValue(option) || ''}
                isOptionEqualToValue={(option, value) => {
                    const optionId = option?.id || option?._id;
                    const valueId = value?.id || value?._id;
                    return optionId === valueId;
                }}
                options={list || []}
                groupBy={groupBy}
                renderGroup={renderGroup}
                key={list}
                value={value}
                inputValue={inputValue}
                disabled={disabled}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                onChange={(event, newValue) => {
                    handleClick(event, newValue);
                    setOpen(false);
                    event?.target?.blur();
                    document?.activeElement?.blur();
                }}
                PaperComponent={
                    CustomPaperComponent
                        ? ({ children, ...paperProps }) => (
                              <Paper {...paperProps}>
                                  {children}
                                  <div style={{ marginTop: 40 }} onMouseDown={(e) => e.preventDefault()}>
                                      {CustomPaperComponent}
                                  </div>
                              </Paper>
                          )
                        : refresh
                          ? ({ children, ...paperProps }) => (
                                <Paper {...paperProps}>
                                    {children}
                                    <div style={{ marginTop: 40 }} onMouseDown={(e) => e.preventDefault()}>
                                        <div className="refresh-btn-wrapper">
                                            <button onClick={() => refresh()} className="refresh-btn">
                                                <div className="refresh-btn-image-box">
                                                    {loader?.length ? (
                                                        <MinLoader
                                                            height={16}
                                                            width={17}
                                                            color={'#0C66E4'}
                                                            margin={'0'}
                                                            position={'relative'}
                                                        />
                                                    ) : (
                                                        <img src={Images.refreshSelect} alt="refresh" />
                                                    )}
                                                </div>
                                                <p>Refresh</p>
                                            </button>
                                        </div>
                                    </div>
                                </Paper>
                            )
                          : undefined
                }
                renderOption={(props, option) => {
                    const { key, ...restProps } = props;
                    return (
                        <li key={key} {...restProps}>
                            {customOptions ? (
                                customOptions(option)
                            ) : wideMenu ? (
                                <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{renderValue(option) || ' '}</span>
                            ) : (
                                <div className="full-width">
                                    <div style={{ width: '100%', gap: '4px' }} className={'selector-wrapper'}>
                                        {option?.img && <img src={option?.img} alt={'icon'} />}
                                        {renderStatus && (
                                            <div className={option?.status === 'ACTIVE' ? 'circle-box green' : 'circle-box red'} />
                                        )}
                                        <TextRow name={renderValue(option) ? renderValue(option) : ' '} textWidth={7} />
                                    </div>
                                </div>
                            )}
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        style={{ color: 'red' }}
                        {...params}
                        slotProps={{
                            input:
                                loader?.length || startIcon
                                    ? {
                                          ...params.InputProps,
                                          endAdornment: !!loader?.length && <InputMinLoader />,
                                          startAdornment: startIcon && <img src={startIcon} alt="icon" />,
                                      }
                                    : { ...params.InputProps },
                        }}
                        placeholder={placeholder}
                        label={title}
                        variant={variant ? variant : 'outlined'}
                        onChange={(e) => handleType && handleType(e)}
                    />
                )}
            />
            {!noError && (
                <ErrMessage text={error === name ? (typeError ? typeError : ErrorText.field) : ''} style={{ position: 'relative' }} />
            )}
        </div>
    );
};
